import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

interface FlyoutItem {
  path: string;
  icon: React.ReactElement;
  label: string;
}

interface SidebarFlyoutProps {
  items: FlyoutItem[];
  triggerRef: React.RefObject<HTMLElement>;
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (path: string) => void;
  activePath: string;
}

const SidebarFlyout: React.FC<SidebarFlyoutProps> = ({
  items,
  triggerRef,
  isOpen,
  onClose,
  onNavigate,
  activePath,
}) => {
  const flyoutRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const hoverTimeout = useRef<NodeJS.Timeout | null>(null);

  // Calculate position based on trigger element
  useEffect(() => {
    if (!isOpen || !triggerRef.current) {
      setPosition(null);
      return;
    }

    const rect = triggerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const flyoutHeight = Math.min(items.length * 44 + 16, viewportHeight * 0.7);
    
    let top = rect.top;
    const left = rect.right + 8;

    // If flyout would overflow bottom, move upward
    if (top + flyoutHeight > viewportHeight - 16) {
      top = Math.max(16, viewportHeight - flyoutHeight - 16);
    }

    setPosition({ top, left });
  }, [isOpen, triggerRef, items.length]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        flyoutRef.current && !flyoutRef.current.contains(target) &&
        triggerRef.current && !triggerRef.current.contains(target)
      ) {
        onClose();
      }
    };
    // Use timeout to avoid closing immediately on the same click that opens
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 10);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, triggerRef]);

  const handleMouseEnter = () => {
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
      hoverTimeout.current = null;
    }
  };

  const handleMouseLeave = () => {
    hoverTimeout.current = setTimeout(() => {
      onClose();
    }, 200);
  };

  const handleItemClick = (path: string) => {
    onNavigate(path);
    onClose();
  };

  if (!isOpen || !position) return null;

  const isActive = (path: string) => activePath === path || activePath.startsWith(path + '/');

  const flyoutContent = (
    <div
      ref={flyoutRef}
      className="sidebar-portal-flyout"
      style={{
        position: 'fixed',
        top: position.top,
        left: position.left,
        zIndex: 99999,
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {items.map((item) => (
        <button
          key={item.path}
          className={`sidebar-flyout-item${isActive(item.path) ? ' active' : ''}`}
          onClick={() => handleItemClick(item.path)}
        >
          <span className="sidebar-flyout-icon">{item.icon}</span>
          <span className="sidebar-flyout-label">{item.label}</span>
        </button>
      ))}
    </div>
  );

  return ReactDOM.createPortal(flyoutContent, document.body);
};

export default SidebarFlyout;
